import { Button, Tooltip, message } from "antd";
import { Mic, MicOff, Video, VideoOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSetCandidateMedia } from "@hooks";
import type { ParticipantMedia } from "./useProctoringRoom";

interface Props {
  examTermId: string;
  examInstanceId: string;
  candidateName: string;
  media?: ParticipantMedia;
  size?: "small" | "middle";
  showLabels?: boolean;
  onSent?: (done: string) => void;
}

/// Remote camera/microphone switches for one named candidate; the new state arrives back as LiveKit mute events.
export const CandidateMediaControls = ({ examTermId, examInstanceId, candidateName, media, size = "small", showLabels = false, onSent }: Props) => {
  const { t } = useTranslation();
  const setMedia = useSetCandidateMedia(examTermId);
  if (!media?.connected) return null;

  const cameraOn = !!media.cameraOn;
  const micOn = !!media.micOn;
  const send = (command: { camera?: boolean; microphone?: boolean }, done: string) =>
    setMedia.mutate(
      { examInstanceId, ...command },
      {
        onSuccess: () => {
          message.success(`${candidateName}: ${done}`);
          onSent?.(done);
        },
        onError: () => message.error(`${candidateName}: ${t("proctoring.commandFailed")}`),
      }
    );
  const iconSize = size === "small" ? 14 : 16;
  const cameraLabel = cameraOn ? t("proctoring.turnCameraOff") : t("proctoring.turnCameraOn");
  const micLabel = micOn ? t("proctoring.turnMicOff") : t("proctoring.turnMicOn");
  const cameraText = cameraOn ? t("proctoring.cameraBtnOff") : t("proctoring.cameraBtnOn");
  const micText = micOn ? t("proctoring.micBtnOff") : t("proctoring.micBtnOn");

  return (
    <span style={{ display: "inline-flex", gap: 8 }} onClick={(e) => e.stopPropagation()}>
      <Tooltip title={`${candidateName}: ${cameraLabel}`}>
        <Button
          size={size}
          danger={cameraOn}
          icon={cameraOn ? <VideoOff size={iconSize} /> : <Video size={iconSize} />}
          loading={setMedia.isPending && setMedia.variables?.camera !== undefined}
          onClick={() => send({ camera: !cameraOn }, cameraOn ? t("proctoring.cameraTurnedOff") : t("proctoring.cameraTurnedOn"))}
          data-testid="proctor-camera-toggle"
        >
          {showLabels && cameraText}
        </Button>
      </Tooltip>
      <Tooltip title={`${candidateName}: ${micLabel}`}>
        <Button
          size={size}
          danger={micOn}
          icon={micOn ? <MicOff size={iconSize} /> : <Mic size={iconSize} />}
          loading={setMedia.isPending && setMedia.variables?.microphone !== undefined}
          onClick={() => send({ microphone: !micOn }, micOn ? t("proctoring.micTurnedOff") : t("proctoring.micTurnedOn"))}
          data-testid="proctor-mic-toggle"
        >
          {showLabels && micText}
        </Button>
      </Tooltip>
    </span>
  );
};
